import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../context/TenantContext';
import { 
  getTemplatesForPlan, 
  getTemplateById, 
  isTemplateAvailable,
  FREE_TEMPLATES,
  PRO_TEMPLATES,
  applyTemplate
} from '../utils/menuTemplates';
import { Lock, Check, Crown, Sparkles, Sun, Moon, Eye, Palette } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';

/**
 * TemplateSelector - Component for selecting menu templates
 * 
 * Features:
 * - Visual template previews
 * - Plan-based restrictions (FREE templates for STARTER, all for PRO+)
 * - Color customization for PRO users
 * - Live preview capability
 */
const TemplateSelector = ({ 
  selectedTemplateId, 
  onTemplateChange,
  customColors = {},
  onColorChange,
  showPreviewLink = true 
}) => {
  const { t } = useTranslation();
  const { tenant, isFeatureEnabled } = useTenant();
  const [filter, setFilter] = useState('all'); // 'all', 'light', 'dark'
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  
  const plan = tenant?.plan || 'STARTER';
  const isPro = plan === 'PRO' || plan === 'ENTERPRISE';
  
  // Get all templates, organized by availability
  const freeTemplates = Object.values(FREE_TEMPLATES);
  const proTemplates = Object.values(PRO_TEMPLATES);
  
  // Filter templates by mode
  const filterTemplates = (templates) => {
    if (filter === 'all') return templates;
    return templates.filter(t => t.mode === filter);
  };

  const filteredFreeTemplates = filterTemplates(freeTemplates);
  const filteredProTemplates = filterTemplates(proTemplates);

  // Handle template selection
  const handleSelectTemplate = (templateId) => {
    if (!isTemplateAvailable(templateId, plan)) {
      // Show upgrade prompt handled by UI
      return;
    }
    onTemplateChange(templateId);
    
    // Apply template for preview
    const template = getTemplateById(templateId);
    if (template) {
      applyTemplate(template, isPro ? customColors : {});
    }
  };

  // Handle color change (PRO only)
  const handleColorChange = (colorKey, value) => {
    if (!isPro) return;
    onColorChange?.({ ...customColors, [colorKey]: value });
    
    // Re-apply template with new colors
    const template = getTemplateById(selectedTemplateId);
    if (template) {
      applyTemplate(template, { ...customColors, [colorKey]: value });
    }
  };

  const TemplateCard = ({ template, isLocked = false }) => {
    const isSelected = selectedTemplateId === template.id;
    const isHovered = hoveredTemplate === template.id;
    
    return (
      <div
        className={`template-card ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
        onClick={() => !isLocked && handleSelectTemplate(template.id)}
        onMouseEnter={() => setHoveredTemplate(template.id)}
        onMouseLeave={() => setHoveredTemplate(null)}
      >
        {/* Template Preview */}
        <div 
          className="template-preview"
          style={{
            background: template.mode === 'dark' ? template.colors.background : template.colors.background,
            borderColor: isSelected ? template.colors.primary : 'transparent',
          }}
        >
          {/* Mini preview of the template */}
          <div className="preview-header" style={{ background: template.colors.headerBg }}>
            <div className="preview-title" style={{ color: template.colors.headerText }}>
              {template.name}
            </div>
          </div>
          <div className="preview-content" style={{ background: template.colors.background }}>
            <div className="preview-category" style={{ 
              background: template.colors.categoryBg,
              color: template.colors.categoryText,
              borderBottom: `2px solid ${template.colors.primary}`
            }}>
              {t('templates.sampleCategory') || 'Category'}
            </div>
            <div className="preview-items">
              {[1, 2, 3].map(i => (
                <div key={i} className="preview-item" style={{ 
                  background: template.colors.cardBg || template.colors.surface,
                  borderRadius: template.spacing.cardRadius,
                }}>
                  {template.layout.mode !== 'no-image' && (
                    <div className="preview-item-image" style={{ background: template.colors.border }} />
                  )}
                  <div className="preview-item-text">
                    <div className="preview-item-name" style={{ background: template.colors.text, opacity: 0.7 }} />
                    <div className="preview-item-desc" style={{ background: template.colors.textMuted, opacity: 0.4 }} />
                  </div>
                  <div className="preview-item-price" style={{ background: template.colors.priceBadge }} />
                </div>
              ))}
            </div>
          </div>
          
          {/* Selected indicator */}
          {isSelected && (
            <div className="selected-badge">
              <Check size={14} />
            </div>
          )}
          
          {/* Locked overlay for PRO templates */}
          {isLocked && (
            <div className="locked-overlay">
              <Lock size={20} />
              <span>{t('templates.proOnly') || 'PRO'}</span>
            </div>
          )}
          
          {/* Mode badge */}
          <div className={`mode-badge ${template.mode}`}>
            {template.mode === 'dark' ? <Moon size={12} /> : <Sun size={12} />}
          </div>
        </div>
        
        {/* Template Info */}
        <div className="template-info">
          <div className="template-name-row">
            <h4 className="template-name">{template.name}</h4>
            {template.tier === 'PRO' && (
              <Badge variant="secondary" className="pro-badge">
                <Crown size={10} className="mr-1" />
                PRO
              </Badge>
            )}
          </div>
          <p className="template-description">
            {template.description}
          </p>
          <p className="template-best-for">
            {t('templates.bestFor') || 'Best for:'} {template.bestFor.join(', ')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="template-selector">
      {/* Header */}
      <div className="selector-header">
        <div>
          <h3 className="selector-title">
            <Sparkles className="inline mr-2" size={20} />
            {t('templates.selectTemplate') || 'Select a Template'}
          </h3>
          <p className="selector-subtitle">
            {t('templates.selectTemplateDesc') || 'Choose a design that matches your restaurant style'}
          </p>
        </div>
        
        {/* Filter buttons */}
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('templates.filterAll') || 'All'}
          </button>
          <button 
            className={`filter-btn ${filter === 'light' ? 'active' : ''}`}
            onClick={() => setFilter('light')}
          >
            <Sun size={14} className="mr-1" />
            {t('templates.filterLight') || 'Light'}
          </button>
          <button 
            className={`filter-btn ${filter === 'dark' ? 'active' : ''}`}
            onClick={() => setFilter('dark')}
          >
            <Moon size={14} className="mr-1" />
            {t('templates.filterDark') || 'Dark'}
          </button>
        </div>
      </div>

      {/* Free Templates Section */}
      <div className="template-section">
        <h4 className="section-title">
          {t('templates.freeTemplates') || 'Free Templates'}
          <Badge variant="outline" className="ml-2">STARTER</Badge>
        </h4>
        <div className="templates-grid">
          {filteredFreeTemplates.map(template => (
            <TemplateCard key={template.id} template={template} />
          ))}
        </div>
      </div>

      {/* PRO Templates Section */}
      <div className="template-section">
        <h4 className="section-title">
          <Crown size={16} className="inline mr-1 text-purple-600" />
          {t('templates.proTemplates') || 'PRO Templates'}
          <Badge className="ml-2 bg-purple-600">PRO</Badge>
        </h4>
        
        {!isPro && (
          <div className="upgrade-banner">
            <div className="upgrade-content">
              <Crown size={24} className="text-purple-600" />
              <div>
                <p className="upgrade-title">{t('templates.unlockProTemplates') || 'Unlock 10 Premium Templates'}</p>
                <p className="upgrade-desc">{t('templates.unlockProTemplatesDesc') || 'Plus custom logo, color customization, and more!'}</p>
              </div>
            </div>
            <Link to="/admin/upgrade">
              <Button className="bg-purple-600 hover:bg-purple-700">
                {t('common.upgrade') || 'Upgrade'}
              </Button>
            </Link>
          </div>
        )}
        
        <div className="templates-grid">
          {filteredProTemplates.map(template => (
            <TemplateCard 
              key={template.id} 
              template={template} 
              isLocked={!isPro}
            />
          ))}
        </div>
      </div>

      {/* Color Customization (PRO only) */}
      {isPro && selectedTemplateId && (
        <div className="color-customization">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette size={18} />
                {t('templates.colorCustomization') || 'Color Customization'}
              </CardTitle>
              <CardDescription>
                {t('templates.colorCustomizationDesc') || 'Customize colors to match your brand'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="color-inputs">
                <div className="color-input-group">
                  <label>{t('templates.primaryColor') || 'Primary Color'}</label>
                  <div className="color-picker-row">
                    <input
                      type="color"
                      value={customColors.primary || getTemplateById(selectedTemplateId)?.colors.primary || '#000000'}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="color-picker"
                    />
                    <Input
                      value={customColors.primary || getTemplateById(selectedTemplateId)?.colors.primary || ''}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      placeholder="#000000"
                      className="color-text-input"
                    />
                  </div>
                </div>
                
                <div className="color-input-group">
                  <label>{t('templates.secondaryColor') || 'Secondary Color'}</label>
                  <div className="color-picker-row">
                    <input
                      type="color"
                      value={customColors.secondary || getTemplateById(selectedTemplateId)?.colors.secondary || '#666666'}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="color-picker"
                    />
                    <Input
                      value={customColors.secondary || getTemplateById(selectedTemplateId)?.colors.secondary || ''}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      placeholder="#666666"
                      className="color-text-input"
                    />
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    onColorChange?.({});
                    const template = getTemplateById(selectedTemplateId);
                    if (template) applyTemplate(template, {});
                  }}
                >
                  {t('templates.resetColors') || 'Reset to Default'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Link */}
      {showPreviewLink && selectedTemplateId && tenant?.slug && (
        <div className="preview-section">
          <a 
            href={(() => {
              const params = new URLSearchParams();
              params.set('preview_template', selectedTemplateId);
              if (customColors?.primary) params.set('preview_primary', customColors.primary);
              if (customColors?.secondary) params.set('preview_secondary', customColors.secondary);
              return `/menu/${tenant.slug}?${params.toString()}`;
            })()}
            target="_blank" 
            rel="noopener noreferrer"
            className="preview-link"
          >
            <Eye size={16} />
            {t('templates.previewMenu') || 'Preview Menu'}
          </a>
        </div>
      )}

      <style>{`
        .template-selector {
          padding: 0;
        }

        .selector-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .selector-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
        }

        .selector-subtitle {
          color: #6b7280;
          font-size: 14px;
          margin: 0;
        }

        .filter-buttons {
          display: flex;
          gap: 8px;
        }

        .filter-btn {
          padding: 6px 12px;
          border: 1px solid hsl(var(--border, 0 0% 89.8%));
          border-radius: 6px;
          background: hsl(var(--background, 0 0% 100%));
          color: hsl(var(--foreground, 0 0% 3.9%));
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          transition: all 0.2s;
        }

        .filter-btn:hover {
          background: hsl(var(--accent, 0 0% 96.1%));
        }

        .filter-btn.active {
          background: hsl(var(--primary, 0 0% 9%));
          color: hsl(var(--primary-foreground, 0 0% 98%));
          border-color: hsl(var(--primary, 0 0% 9%));
        }

        .template-section {
          margin-bottom: 32px;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
        }

        .templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .template-card {
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .template-card:hover:not(.locked) {
          border-color: #9ca3af;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .template-card.selected {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2);
        }

        .template-card.locked {
          cursor: not-allowed;
          opacity: 0.85;
        }

        .template-preview {
          position: relative;
          height: 180px;
          overflow: hidden;
          border-bottom: 1px solid #e5e7eb;
        }

        .preview-header {
          padding: 8px 12px;
          display: flex;
          align-items: center;
        }

        .preview-title {
          font-size: 11px;
          font-weight: 600;
        }

        .preview-content {
          padding: 8px;
          height: calc(100% - 36px);
        }

        .preview-category {
          font-size: 9px;
          font-weight: 600;
          padding: 4px 8px;
          margin-bottom: 8px;
          border-radius: 4px;
        }

        .preview-items {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .preview-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
        }

        .preview-item-image {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .preview-item-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .preview-item-name {
          height: 6px;
          width: 60%;
          border-radius: 2px;
        }

        .preview-item-desc {
          height: 4px;
          width: 80%;
          border-radius: 2px;
        }

        .preview-item-price {
          width: 24px;
          height: 10px;
          border-radius: 3px;
        }

        .selected-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #2563eb;
          color: white;
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .locked-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: white;
          font-weight: 600;
          font-size: 13px;
        }

        .mode-badge {
          position: absolute;
          bottom: 8px;
          right: 8px;
          padding: 4px 6px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          font-size: 10px;
        }

        .mode-badge.light {
          background: #fef3c7;
          color: #92400e;
        }

        .mode-badge.dark {
          background: #374151;
          color: #f3f4f6;
        }

        .template-info {
          padding: 12px 16px;
        }

        .template-name-row {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }

        .template-name {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
        }

        .pro-badge {
          font-size: 10px;
          padding: 2px 6px;
        }

        .template-description {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 4px 0;
          line-height: 1.4;
        }

        .template-best-for {
          font-size: 11px;
          color: #9ca3af;
          margin: 0;
        }

        .upgrade-banner {
          background: linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%);
          border: 1px solid #c4b5fd;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .upgrade-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .upgrade-title {
          font-weight: 600;
          color: #6b21a8;
          margin: 0;
        }

        .upgrade-desc {
          font-size: 13px;
          color: #7c3aed;
          margin: 0;
        }

        .color-customization {
          margin-top: 24px;
        }

        .color-inputs {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          align-items: flex-end;
        }

        .color-input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .color-input-group label {
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }

        .color-picker-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .color-picker {
          width: 40px;
          height: 40px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          padding: 2px;
        }

        .color-text-input {
          width: 120px;
        }

        .preview-section {
          margin-top: 24px;
          text-align: center;
        }

        .preview-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: hsl(var(--accent, 0 0% 96.1%));
          border-radius: 8px;
          color: hsl(var(--foreground, 0 0% 3.9%));
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s;
        }

        .preview-link:hover {
          background: hsl(var(--muted, 0 0% 89.8%));
        }

        @media (max-width: 640px) {
          .templates-grid {
            grid-template-columns: 1fr;
          }

          .selector-header {
            flex-direction: column;
          }

          .upgrade-banner {
            flex-direction: column;
            text-align: center;
          }

          .upgrade-content {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default TemplateSelector;
